import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, EMPTY, of } from 'rxjs';

import { UserInfo } from '@blaze-shared/api-contracts-domain';
import {
  appStateActions,
  BlazeAccountFacade,
  ProjectContextFacade,
} from '@blaze-shared/app-state-data-access';
import { UsersApiService, UsersCollectionService } from '@blaze-shared/auth-data-access';
import { sentryLog } from '@blaze-shared/logger-util';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { ButtonComponent, ButtonIconComponent, DialogComponent } from '@blaze-shared/shared-ui';
import { toRatio } from '@blaze-shared/shared-util';
import { UploaderFacade, UploaderOptions } from '@blaze-shared/uploader';

import { TwoFactorAuthComponent } from '../two-factor-auth/two-factor-auth.component';

interface UserProfileGroup {
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
}

@UntilDestroy()
@Component({
  imports: [
    ButtonComponent,
    DialogComponent,
    FormsModule,
    MatIconModule,
    ReactiveFormsModule,
    TwoFactorAuthComponent,
    ButtonIconComponent,
  ],
  selector: 'blaze-user-profile-dialog',
  templateUrl: './user-profile-dialog.component.html',
  styleUrls: ['./user-profile-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileDialogComponent implements OnInit {
  private readonly blazeAccountFacade = inject(BlazeAccountFacade);
  private readonly dialogRef = inject(MatDialogRef<UserProfileDialogComponent>);
  private readonly notificationService = inject(NotificationService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly store = inject<Store<any>>(Store);
  private readonly uploader = inject(UploaderFacade);
  private readonly usersApiService = inject(UsersApiService);
  private readonly usersCollService = inject(UsersCollectionService);

  protected readonly acceptedImageExtensions = ['.jpg', '.png', '.gif', '.bmp'];
  protected readonly avatarSrc = signal('');
  protected readonly isDialogVisible = signal(true);
  protected readonly user = signal<UserInfo | undefined>(undefined);

  protected userHasPasswordLogin: Signal<boolean>;

  protected userProfileForm: FormGroup<UserProfileGroup>;

  constructor() {
    this.initSignals();
  }

  ngOnInit(): void {
    this.projectContextFacade.userInfo$
      .pipe(debounceTime(50), untilDestroyed(this))
      .subscribe((userInfo) => {
        this.user.set(userInfo);
        this.avatarSrc.set(this.user()?.profilePicture || '');
        this.updateFormValuesFromUser(userInfo);
      });
    this.initForm();
  }

  onChangePassword(): void {
    if (!this?.user()?.email) {
      sentryLog('User logged in but email unknown. Unable to change password', 'error');
      return;
    }
    this.usersApiService
      .postForgotPassword(this.user()?.email || '')
      .pipe(
        catchError((err) => {
          this.notificationService.showError(err);
          return of({ email: this.user()?.email, link: '' });
        }),
      )
      .subscribe((response) => {
        if (!response?.link) {
          this.notificationService.info(
            `Please wait at least 5 minutes from your last attempt to reset password.`,
          );
          return;
        }
        if (response?.link) {
          console.log(`Redirecting to ${response.link}`);
          globalThis.location.href = response.link;
        }
      });
  }

  protected onAvatarDelete(): void {
    this.avatarSrc.set('');
    this.userProfileForm.markAllAsDirty();
  }

  protected onCloseDialog(): void {
    this.dialogRef.close();
  }

  protected onFileSelected(fileInput: EventTarget | null): void {
    const fileInputElement = fileInput as HTMLInputElement;
    const file = fileInputElement?.files?.[0];
    fileInputElement.value = '';
    if (!file) {
      return;
    }
    this.upload(file);
  }

  protected onSaveUserProfile(): void {
    const user = this.user();
    if (!user?.id) {
      console.error('Unable to update user. User ID unknonw', this.user);
      return;
    }
    this.userProfileForm.markAsPristine();
    this.usersApiService
      .putUser(user.id, {
        firstName: this.userProfileForm.get('firstName')?.value?.trim() || '',
        lastName: this.userProfileForm.get('lastName')?.value?.trim() || '',
        profilePicture: this.avatarSrc(),
      })
      .pipe(
        catchError((err) => {
          this.notificationService.showError(err);
          this.userProfileForm.markAsDirty();
          return EMPTY;
        }),
      )
      .subscribe((user) => {
        if (!user) {
          return;
        }
        this.user.set(user);
        this.usersCollService.updateOneInCache(user);
        this.store.dispatch(appStateActions.updateUserInfo({ user }));
      });
  }
  protected onSecondaryDialogVisible(isSecondaryVisible: boolean): void {
    this.isDialogVisible.set(!isSecondaryVisible);
  }

  private upload(preselectFile: File): void {
    const preselectFiles: File[] | undefined = preselectFile ? [preselectFile] : undefined;

    const options: UploaderOptions = {
      accountId: this.blazeAccountFacade.getAccountId(),
      acceptedFileTypes: this.acceptedImageExtensions,
      cropForceAspectRatio: toRatio('1:1'),
      forceCrop: true,
      isMultipleSelection: false,
      preselectFiles,
    };

    this.uploader.upload(options).subscribe((result) => {
      const url = result?.[0]?.url;
      if (!url) {
        return;
      }
      this.avatarSrc.set(url);
      this.userProfileForm.markAllAsDirty();
    });
  }

  private initForm(): void {
    this.userProfileForm = new FormGroup<UserProfileGroup>({
      firstName: new FormControl<string>(this.user()?.firstName || '', {
        nonNullable: true,
        validators: [this.validateRequiredTrimmed.bind(this)],
      }),
      lastName: new FormControl<string>(this.user()?.lastName || '', {
        nonNullable: true,
        validators: [this.validateRequiredTrimmed.bind(this)],
      }),
      email: new FormControl<string>(this.user()?.email || '', {
        nonNullable: true,
        validators: [this.validateRequiredTrimmed.bind(this)],
      }),
    });
    this.userProfileForm.get('email')?.disable();
  }

  private initSignals(): void {
    this.userHasPasswordLogin = computed(() => {
      return !!this.user()?.loginTypes?.includes('Password');
    });

    effect(() => {
      this.userProfileForm.patchValue({
        firstName: this.user()?.firstName || '',
        lastName: this.user()?.lastName || '',
      });
    });
  }

  private updateFormValuesFromUser(userInfo: UserInfo | undefined): void {
    this.userProfileForm.patchValue({
      firstName: userInfo?.firstName || '',
      lastName: userInfo?.lastName || '',
      email: userInfo?.email || '',
    });
  }

  private validateRequiredTrimmed(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || (typeof value === 'string' && value.trim().length === 0)) {
      return { required: true };
    }
    return null;
  }
}
